import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Stack,
  Grid,
  Typography,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  SvgIcon,
} from '@mui/material';
import { logoutUser } from './headerActions';
import { AccountCircleOutlined } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { set_user } from '../../Store/reducers/userReducer';
import { AppDispatch, RootState } from '../../Store/Store';
import { UserType } from '../../shared/types/User.types';

const drawerWidth = '208px';
const classes = {
  content: {
    flexGrow: 1,
    backgroundColor: '#F8F8F8',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
    backgroundPosition: 'center',
    overflow: 'auto',
    height: '100vh',
  },
  link: {
    padding: '8px 0',
    opacity: 0.8,
    '& div span': {
      color: '#fff',
      fontFamily: 'Heineken Core',
      lineHeight: '22px',
    },
    '& div svg': {
      fill: '#fff',
      marginRight: '8px',
      opacity: 0.8,
    },
    '&:hover': {
      opacity: 1,
      'div svg': {
        opacity: 1,
      },
    },
  },
  selectedNav: {
    padding: '8px 0',
    '& div span': {
      color: '#fff',
      fontFamily: 'Heineken Core',
      lineHeight: '22px',
    },
    '& div svg': {
      fill: '#fff',
      marginRight: '8px',
    },
  },
  topContainer: {
    minHeight: 70,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },

  itemIcons: {
    minWidth: 0,
  },
};

const Header: React.FC<{ children: React.ReactElement }> = ({ children }): React.ReactElement => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(({ user }: RootState) => user.user);
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutUser = () => {
    dispatch(logoutUser()).then(() => {
      handleClose();
    });
  };

  useEffect(() => {
    const localStorageUser = localStorage.getItem('user')
      ? localStorage.getItem('user') === 'undefined'
        ? '{}'
        : localStorage.getItem('user') || '{}'
      : '{}';

    if (localStorageUser && localStorageUser !== 'undefined') {
      dispatch(set_user(JSON.parse(localStorageUser)));
    } else {
      handleLogoutUser();
    }
  }, []);

  useEffect(() => {
    dispatch(set_user(user));
  }, [user, dispatch]);

  return user && user?.id ? (
    <Box sx={{ display: 'flex' }}>
      <Drawer
        variant="permanent"
        anchor="left"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        PaperProps={{
          style: {
            backgroundColor: '#13670B',
            borderRight: 0,
          },
        }}>
        <Stack
          justifyContent="space-between"
          sx={{
            height: '100%',
            padding: '24px',
          }}>
          <List sx={{ mb: '32px', justifyContent: 'center' }}>
            <ListItem
              sx={{ mb: '24px', justifyContent: 'center' }}
              component={Link}
              to="/sponsorships">
              <Grid item>
                <Grid container direction="column" spacing={0}>
                  <Grid item style={{ textAlign: 'center' }}>
                    <img
                      style={{ width: 160, height: 'auto' }}
                      alt="Heineken Logo White 1"
                      src="/images/HNK_PNG-1.svg"
                    />
                  </Grid>
                  <Grid item style={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="secondary" fontSize={12}>
                      Sponsorship Evaluation Tool
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </ListItem>
            <ListItem
              button
              component={Link}
              to="/sponsorships"
              sx={
                location.pathname.startsWith('/sponsorships') ? classes.selectedNav : classes.link
              }>
              <ListItemIcon sx={classes.itemIcons}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none">
                  <path
                    d="M11.8752 20.3359C11.9419 20.3359 12.0086 20.3193 12.0752 20.2859C12.1419 20.2526 12.1919 20.2193 12.2252 20.1859L20.4252 11.9859C20.6252 11.7859 20.7711 11.5609 20.8627 11.3109C20.9544 11.0609 21.0002 10.8109 21.0002 10.5609C21.0002 10.2943 20.9544 10.0401 20.8627 9.79844C20.7711 9.55677 20.6252 9.34427 20.4252 9.16094L16.1752 4.91094C15.9919 4.71094 15.7794 4.5651 15.5377 4.47344C15.2961 4.38177 15.0419 4.33594 14.7752 4.33594C14.5252 4.33594 14.2752 4.38177 14.0252 4.47344C13.7752 4.5651 13.5502 4.71094 13.3502 4.91094L13.0752 5.18594L14.9252 7.06094C15.1752 7.29427 15.3586 7.56094 15.4752 7.86094C15.5919 8.16094 15.6502 8.4776 15.6502 8.81094C15.6502 9.51094 15.4127 10.0984 14.9377 10.5734C14.4627 11.0484 13.8752 11.2859 13.1752 11.2859C12.8419 11.2859 12.5211 11.2276 12.2127 11.1109C11.9044 10.9943 11.6336 10.8193 11.4002 10.5859L9.52523 8.73594L5.15023 13.1109C5.10023 13.1609 5.06273 13.2151 5.03773 13.2734C5.01273 13.3318 5.00023 13.3943 5.00023 13.4609C5.00023 13.5943 5.05023 13.7151 5.15023 13.8234C5.25023 13.9318 5.3669 13.9859 5.50023 13.9859C5.5669 13.9859 5.63357 13.9693 5.70023 13.9359C5.7669 13.9026 5.8169 13.8693 5.85023 13.8359L9.25023 10.4359L10.6502 11.8359L7.27523 15.2359C7.22523 15.2859 7.18773 15.3401 7.16273 15.3984C7.13773 15.4568 7.12523 15.5193 7.12523 15.5859C7.12523 15.7193 7.17523 15.8359 7.27523 15.9359C7.37523 16.0359 7.4919 16.0859 7.62523 16.0859C7.6919 16.0859 7.75857 16.0693 7.82523 16.0359C7.8919 16.0026 7.9419 15.9693 7.97523 15.9359L11.3752 12.5609L12.7752 13.9609L9.40023 17.3609C9.35023 17.3943 9.31273 17.4443 9.28773 17.5109C9.26273 17.5776 9.25023 17.6443 9.25023 17.7109C9.25023 17.8443 9.30023 17.9609 9.40023 18.0609C9.50023 18.1609 9.6169 18.2109 9.75023 18.2109C9.8169 18.2109 9.8794 18.1984 9.93773 18.1734C9.99607 18.1484 10.0502 18.1109 10.1002 18.0609L13.5002 14.6859L14.9002 16.0859L11.5002 19.4859C11.4502 19.5359 11.4127 19.5901 11.3877 19.6484C11.3627 19.7068 11.3502 19.7693 11.3502 19.8359C11.3502 19.9693 11.4044 20.0859 11.5127 20.1859C11.6211 20.2859 11.7419 20.3359 11.8752 20.3359ZM11.8502 22.3359C11.2336 22.3359 10.6877 22.1318 10.2127 21.7234C9.73773 21.3151 9.45857 20.8026 9.37523 20.1859C8.80857 20.1026 8.33357 19.8693 7.95023 19.4859C7.5669 19.1026 7.33357 18.6276 7.25023 18.0609C6.68357 17.9776 6.21273 17.7401 5.83773 17.3484C5.46273 16.9568 5.23357 16.4859 5.15023 15.9359C4.5169 15.8526 4.00023 15.5776 3.60023 15.1109C3.20023 14.6443 3.00023 14.0943 3.00023 13.4609C3.00023 13.1276 3.06273 12.8068 3.18773 12.4984C3.31273 12.1901 3.4919 11.9193 3.72523 11.6859L9.52523 5.91094L12.8002 9.18594C12.8336 9.23594 12.8836 9.27344 12.9502 9.29844C13.0169 9.32344 13.0836 9.33594 13.1502 9.33594C13.3002 9.33594 13.4252 9.2901 13.5252 9.19844C13.6252 9.10677 13.6752 8.98594 13.6752 8.83594C13.6752 8.76927 13.6627 8.7026 13.6377 8.63594C13.6127 8.56927 13.5752 8.51927 13.5252 8.48594L9.95023 4.91094C9.7669 4.71094 9.5544 4.5651 9.31273 4.47344C9.07107 4.38177 8.8169 4.33594 8.55023 4.33594C8.30023 4.33594 8.05023 4.38177 7.80023 4.47344C7.55023 4.5651 7.32523 4.71094 7.12523 4.91094L3.60023 8.46094C3.45023 8.61094 3.32523 8.78594 3.22523 8.98594C3.12523 9.18594 3.05857 9.38594 3.02523 9.58594C2.9919 9.78594 2.9919 9.9901 3.02523 10.1984C3.05857 10.4068 3.12523 10.6026 3.22523 10.7859L1.77523 12.2359C1.4919 11.8526 1.28357 11.4318 1.15023 10.9734C1.0169 10.5151 0.966901 10.0526 1.00023 9.58594C1.03357 9.11927 1.15023 8.6651 1.35023 8.22344C1.55023 7.78177 1.82523 7.38594 2.17523 7.03594L5.70023 3.51094C6.10023 3.1276 6.54607 2.83594 7.03773 2.63594C7.5294 2.43594 8.03357 2.33594 8.55023 2.33594C9.0669 2.33594 9.57107 2.43594 10.0627 2.63594C10.5544 2.83594 10.9919 3.1276 11.3752 3.51094L11.6502 3.78594L11.9252 3.51094C12.3252 3.1276 12.7711 2.83594 13.2627 2.63594C13.7544 2.43594 14.2586 2.33594 14.7752 2.33594C15.2919 2.33594 15.7961 2.43594 16.2877 2.63594C16.7794 2.83594 17.2169 3.1276 17.6002 3.51094L21.8252 7.73594C22.2086 8.11927 22.5002 8.56094 22.7002 9.06094C22.9002 9.56094 23.0002 10.0693 23.0002 10.5859C23.0002 11.1026 22.9002 11.6068 22.7002 12.0984C22.5002 12.5901 22.2086 13.0276 21.8252 13.4109L13.6252 21.5859C13.3919 21.8193 13.1211 22.0026 12.8127 22.1359C12.5044 22.2693 12.1836 22.3359 11.8502 22.3359Z"
                    fill="white"
                  />
                </svg>
              </ListItemIcon>
              <ListItemText primary="Sponsorships" />
            </ListItem>
            <ListItem
              button
              component={Link}
              to="/benchmarking"
              sx={
                location.pathname.startsWith('/benchmarking') ? classes.selectedNav : classes.link
              }>
              <ListItemIcon sx={classes.itemIcons}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none">
                  <path
                    d="M3 21.3359V19.3359L5 17.3359V21.3359H3ZM7 21.3359V15.3359L9 13.3359V21.3359H7ZM11 21.3359V13.3359L13 15.3609V21.3359H11ZM15 21.3359V15.3609L17 13.3609V21.3359H15ZM19 21.3359V11.3359L21 9.33594V21.3359H19ZM3 16.1609V13.3359L10 6.33594L14 10.3359L21 3.33594V6.16094L14 13.1609L10 9.16094L3 16.1609Z"
                    fill="white"
                  />
                </svg>
              </ListItemIcon>
              <ListItemText primary="Benchmarking" />
            </ListItem>
            <ListItem
              button
              component={Link}
              to="/audience-insight"
              sx={
                location.pathname.startsWith('/audience-insight')
                  ? classes.selectedNav
                  : classes.link
              }>
              <ListItemIcon sx={classes.itemIcons}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none">
                  <path
                    d="M7.8 5.56094L6.8 2.56094L8.2 2.11094L9.2 5.08594L7.8 5.56094ZM11.25 4.33594V1.33594H12.75V4.33594H11.25ZM16.2 5.56094L14.8 5.08594L15.8 2.11094L17.2 2.58594L16.2 5.56094ZM2.15 23.3359L1.85 21.3609L7.125 20.5609C7.30833 20.5276 7.47083 20.4484 7.6125 20.3234C7.75417 20.1984 7.85833 20.0443 7.925 19.8609L8.775 17.2109C8.85833 16.9776 8.85833 16.7526 8.775 16.5359C8.69167 16.3193 8.54167 16.1526 8.325 16.0359L7.5 18.6359L5.6 18.0359L7.8 11.0859C7.83333 10.9859 7.85 10.8776 7.85 10.7609C7.85 10.6443 7.83333 10.5359 7.8 10.4359L4.45 16.7359C4.18333 17.2193 3.8125 17.6068 3.3375 17.8984C2.8625 18.1901 2.35 18.3359 1.8 18.3359H1V16.3359H1.8C1.98333 16.3359 2.15417 16.2901 2.3125 16.1984C2.47083 16.1068 2.59167 15.9776 2.675 15.8109L7.1 7.46094L8.35 8.16094C8.96667 8.51094 9.40417 9.0151 9.6625 9.67344C9.92083 10.3318 9.94167 11.0026 9.725 11.6859L8.95 14.1359C9.68333 14.4193 10.2125 14.9193 10.5375 15.6359C10.8625 16.3526 10.9083 17.0859 10.675 17.8359L9.825 20.4859C9.64167 21.0193 9.3375 21.4734 8.9125 21.8484C8.4875 22.2234 7.99167 22.4526 7.425 22.5359L2.15 23.3359ZM21.85 23.3359L16.575 22.5359C16.0083 22.4526 15.5125 22.2234 15.0875 21.8484C14.6625 21.4734 14.3583 21.0193 14.175 20.4859L13.325 17.8359C13.0917 17.0859 13.1375 16.3526 13.4625 15.6359C13.7875 14.9193 14.3167 14.4193 15.05 14.1359L14.275 11.6859C14.0583 11.0026 14.0792 10.3318 14.3375 9.67344C14.5958 9.0151 15.0333 8.51094 15.65 8.16094L16.9 7.46094L21.325 15.8109C21.4083 15.9776 21.5292 16.1068 21.6875 16.1984C21.8458 16.2901 22.0167 16.3359 22.2 16.3359H23V18.3359H22.2C21.65 18.3359 21.1375 18.1901 20.6625 17.8984C20.1875 17.6068 19.8167 17.2193 19.55 16.7359L16.2 10.4359C16.1667 10.5359 16.15 10.6443 16.15 10.7609C16.15 10.8776 16.1667 10.9859 16.2 11.0859L18.4 18.0359L16.5 18.6359L15.675 16.0359C15.4583 16.1526 15.3083 16.3193 15.225 16.5359C15.1417 16.7526 15.1417 16.9776 15.225 17.2109L16.075 19.8609C16.1417 20.0443 16.2458 20.1984 16.3875 20.3234C16.5292 20.4484 16.6917 20.5276 16.875 20.5609L22.15 21.3609L21.85 23.3359Z"
                    fill="white"
                  />
                </svg>
              </ListItemIcon>
              <ListItemText primary="Audience Insight" />
            </ListItem>
            <ListItem
              button
              component={Link}
              to="/sponsorship-screeners"
              sx={
                location.pathname.startsWith('/sponsorship-screeners')
                  ? classes.selectedNav
                  : classes.link
              }>
              <ListItemIcon sx={classes.itemIcons}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="21"
                  viewBox="0 0 20 21"
                  fill="none">
                  <path
                    d="M10 20.3369C8.61667 20.3369 7.31667 20.0744 6.1 19.5494C4.88333 19.0244 3.825 18.3119 2.925 17.4119C2.025 16.5119 1.3125 15.4536 0.7875 14.2369C0.2625 13.0202 0 11.7202 0 10.3369C0 8.95358 0.2625 7.65358 0.7875 6.43691C1.3125 5.22025 2.025 4.16191 2.925 3.26191C3.825 2.36191 4.88333 1.64941 6.1 1.12441C7.31667 0.599414 8.61667 0.336914 10 0.336914C11.3833 0.336914 12.6833 0.599414 13.9 1.12441C15.1167 1.64941 16.175 2.36191 17.075 3.26191C17.975 4.16191 18.6875 5.22025 19.2125 6.43691C19.7375 7.65358 20 8.95358 20 10.3369C20 11.7202 19.7375 13.0202 19.2125 14.2369C18.6875 15.4536 17.975 16.5119 17.075 17.4119C16.175 18.3119 15.1167 19.0244 13.9 19.5494C12.6833 20.0744 11.3833 20.3369 10 20.3369ZM10 18.3369C10.9333 18.3369 11.8125 18.1911 12.6375 17.8994C13.4625 17.6077 14.2167 17.1952 14.9 16.6619L13.475 15.2369C12.9917 15.5869 12.4542 15.8577 11.8625 16.0494C11.2708 16.2411 10.65 16.3369 10 16.3369C8.33333 16.3369 6.91667 15.7536 5.75 14.5869C4.58333 13.4202 4 12.0036 4 10.3369C4 8.67025 4.58333 7.25358 5.75 6.08691C6.91667 4.92025 8.33333 4.33691 10 4.33691C11.6667 4.33691 13.0833 4.92025 14.25 6.08691C15.4167 7.25358 16 8.67025 16 10.3369C16 10.9869 15.9 11.6119 15.7 12.2119C15.5 12.8119 15.225 13.3536 14.875 13.8369L16.3 15.2619C16.8333 14.5786 17.25 13.8202 17.55 12.9869C17.85 12.1536 18 11.2702 18 10.3369C18 8.10358 17.225 6.21191 15.675 4.66191C14.125 3.11191 12.2333 2.33691 10 2.33691C7.76667 2.33691 5.875 3.11191 4.325 4.66191C2.775 6.21191 2 8.10358 2 10.3369C2 12.5702 2.775 14.4619 4.325 16.0119C5.875 17.5619 7.76667 18.3369 10 18.3369ZM10 14.3369C10.3667 14.3369 10.7208 14.2911 11.0625 14.1994C11.4042 14.1077 11.725 13.9702 12.025 13.7869L10.5 12.2619C10.4167 12.2952 10.3333 12.3161 10.25 12.3244C10.1667 12.3327 10.0833 12.3369 10 12.3369C9.45 12.3369 8.97917 12.1411 8.5875 11.7494C8.19583 11.3577 8 10.8869 8 10.3369C8 9.78691 8.19583 9.31608 8.5875 8.92441C8.97917 8.53275 9.45 8.33691 10 8.33691C10.55 8.33691 11.0208 8.53275 11.4125 8.92441C11.8042 9.31608 12 9.78691 12 10.3369C12 10.4369 11.9958 10.5327 11.9875 10.6244C11.9792 10.7161 11.9583 10.8036 11.925 10.8869L13.425 12.3869C13.6083 12.0869 13.75 11.7661 13.85 11.4244C13.95 11.0827 14 10.7202 14 10.3369C14 9.23691 13.6083 8.29525 12.825 7.51191C12.0417 6.72858 11.1 6.33691 10 6.33691C8.9 6.33691 7.95833 6.72858 7.175 7.51191C6.39167 8.29525 6 9.23691 6 10.3369C6 11.4369 6.39167 12.3786 7.175 13.1619C7.95833 13.9452 8.9 14.3369 10 14.3369Z"
                    fill="white"
                  />
                </svg>
              </ListItemIcon>
              <ListItemText primary="Sponsorship Screener" />
            </ListItem>
          </List>

          {user.is_admin ? (
            <List>
              <Grid container direction="row" wrap="nowrap" alignItems="flex-end">
                <Grid item>
                  <ListItem
                    sx={
                      location.pathname.startsWith('/admin') ? classes.selectedNav : classes.link
                    }>
                    <ListItemIcon sx={classes.itemIcons}>
                      <SvgIcon component={AccountCircleOutlined} />
                    </ListItemIcon>
                  </ListItem>
                </Grid>
                <Grid item>
                  <Grid container direction="row" spacing={2} alignItems="center">
                    <Button
                      onClick={handleClick}
                      sx={
                        location.pathname.startsWith('/admin') ? classes.selectedNav : classes.link
                      }>
                      {user?.name}
                    </Button>
                    <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                      <MenuItem
                        onClick={() => {
                          handleLogoutUser();
                        }}>
                        Logout
                      </MenuItem>
                    </Menu>
                  </Grid>
                </Grid>
                <Grid item>
                  <ListItem
                    button
                    component={Link}
                    to="/admin"
                    sx={
                      location.pathname.startsWith('/admin') ? classes.selectedNav : classes.link
                    }>
                    <ListItemText primary={`/Admin`} />
                  </ListItem>
                </Grid>
              </Grid>
            </List>
          ) : (
            <List>
              <ListItem>
                <Grid container direction="row" spacing={2} alignItems="center">
                  <Button
                    onClick={handleClick}
                    sx={classes.link}
                    startIcon={<AccountCircleOutlined />}>
                    {user?.name}
                  </Button>
                  <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    <MenuItem
                      onClick={() => {
                        handleLogoutUser();
                      }}>
                      Logout
                    </MenuItem>
                  </Menu>
                </Grid>
              </ListItem>
            </List>
          )}
        </Stack>
      </Drawer>
      <Box component="main" sx={classes.content}>
        <Grid container direction="column" justifyContent="center">
          <Grid item>{children}</Grid>
        </Grid>
      </Box>
    </Box>
  ) : (
    <>{children}</>
  );
};

export default Header;
